import Phaser from 'phaser';
import { TILE_SIZE } from '@shared/constants';
import { getResourceNodeType, getNodeProduceEntries } from '@shared/data/resourceNodes';
import { getItemTemplate } from '@shared/data/items';
import type { ResourceNodeStateView } from '../../state/GameStateView';
import { ResourceNodeTooltip } from './ResourceNodeTooltip';

/**
 * Dwell del tooltip: al pasar el mouse por un nodo, el tooltip recién aparece tras dejarlo encima
 * este tiempo. Mismo valor que el hover del pin de skills (SkillsPanel.PIN_HOVER_DELAY).
 */
const NODE_HOVER_DELAY = 1000;

/**
 * Renderer de yacimientos (nodos de recurso). Dibuja cada nodo del state con el
 * sprite real del yacimiento (`ore_node_<tier>_<stage>`, en assets/world/nodes/),
 * eligiendo el FRAME por cuánto queda: full → mid → low → depleted, según
 * `remaining/maxQuantity`. Al agotarse (`depleted`) usa el frame gris + una
 * partícula/pulso sutil hasta que respawnea. Si falta la textura, cae a un
 * "peñasco" procedural (Graphics) coloreado por tier. Click → intenta extraer.
 *
 * Los sprites se generan tinteando `mining/deposit1-3.png` por tier con
 * client/scripts/gen_tinted_assets.py. Ver docs/MINING_AND_SMITHING.md.
 */

/** Etapa visual del yacimiento según cuánto material queda. */
function nodeStage(remaining: number, maxQuantity: number, depleted: boolean): string {
  if (depleted) return 'depleted';
  const ratio = maxQuantity > 0 ? remaining / maxQuantity : 1;
  if (ratio > 0.66) return 'full';
  if (ratio > 0.33) return 'mid';
  return 'low';
}

/** Color base del peñasco por tier de mineral. */
const TIER_COLORS: Record<string, number> = {
  bronze: 0x9c6b3f,
  iron: 0x8f8f8f,
  steel: 0xcfd3dc,
  mithril: 0x4f7fd6,
  adamant: 0x3fae72,
  rune: 0x36b6c9,
  obsidian: 0x4a2b66, // violeta oscuro (distinto del carbón negro)
};

interface NodeVisual {
  container: Phaser.GameObjects.Container;
  rock: Phaser.GameObjects.Graphics; // fallback procedural
  sprite?: Phaser.GameObjects.Sprite; // arte real del yacimiento (si existe textura)
  pulse?: Phaser.GameObjects.Arc;
  pulseTween?: Phaser.Tweens.Tween;
  areaHighlight?: Phaser.GameObjects.Graphics; // resaltado sutil del área (cardúmenes)
  areaFishIcons?: Phaser.GameObjects.Sprite[]; // íconos de los peces (baja opacidad) al centro del área
  areaTween?: Phaser.Tweens.Tween;
  lastKey: string; // para redibujar sólo si cambió algo visual
  node?: ResourceNodeStateView; // último state (para el mensaje de consola al click)
}

/** Sólo lo que necesitamos de GameScene para el depth (evita import circular). */
interface DepthScene {
  getEntityBaseDepthFromTileY(tileY: number): number;
}

export class ResourceNodeRenderer {
  private visuals = new Map<string, NodeVisual>();
  /** Tooltip de hover (qué da el nodo + prob + nivel + cantidad total). Único, reusable. */
  private tooltip: ResourceNodeTooltip;
  /** Timer de dwell del hover (setTimeout id). Sólo uno activo a la vez. */
  private hoverTimer: number | null = null;

  constructor(
    private scene: Phaser.Scene,
    private onGather: (nodeId: string) => void,
    // Al hacer click en un nodo, loguea su nombre + cantidad (actual/total) en la
    // consola, igual que los items al inspeccionar un tile. Opcional.
    private onInspect?: (text: string) => void
  ) {
    this.tooltip = new ResourceNodeTooltip(scene);
  }

  /** Cancela el dwell pendiente del hover (si lo hay). */
  private cancelHover(): void {
    if (this.hoverTimer !== null) {
      window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  /**
   * Depth del yacimiento: usa la MISMA convención que las entidades del juego
   * (`GameScene.getEntityBaseDepthFromTileY`) para que se ordene por fila con
   * NPCs/decoraciones (el player pasa por delante/detrás bien) y no "flote" con
   * un depth gigante (worldY en px). Fallback: tileY si el método no existe.
   */
  private depthFor(tileY: number): number {
    const s = this.scene as unknown as Partial<DepthScene>;
    return typeof s.getEntityBaseDepthFromTileY === 'function'
      ? s.getEntityBaseDepthFromTileY(tileY)
      : tileY;
  }

  /** Crea o actualiza el visual de un nodo. */
  public addOrUpdate(id: string, node: ResourceNodeStateView): void {
    const worldX = node.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = node.y * TILE_SIZE + TILE_SIZE / 2;
    const app = this.nodeAppearance(node);
    const key = app.texKey; // captura tier/especie + etapa + variante (para redibujar)

    let visual = this.visuals.get(id);
    if (!visual) {
      const container = this.scene.add.container(worldX, worldY);
      container.setDepth(this.depthFor(node.y)); // se ordena como una entidad en su tile
      const rock = this.scene.add.graphics();
      container.add(rock);
      // Hit area interactiva (click → extraer).
      container.setSize(TILE_SIZE, TILE_SIZE);
      container.setInteractive(
        new Phaser.Geom.Rectangle(-TILE_SIZE / 2, -TILE_SIZE / 2, TILE_SIZE, TILE_SIZE),
        Phaser.Geom.Rectangle.Contains
      );
      container.on('pointerover', () => {
        this.scene.input.setDefaultCursor('pointer');
        // Dwell: el tooltip recién aparece tras dejar el mouse encima NODE_HOVER_DELAY ms,
        // igual que el hover del pin de skills. Se muestra sólo si el nodo sigue vivo.
        this.cancelHover();
        this.hoverTimer = window.setTimeout(() => {
          this.hoverTimer = null;
          const n = this.visuals.get(id)?.node;
          if (!n) return;
          const p = this.scene.input.activePointer;
          this.tooltip.show(n, p.x, p.y);
        }, NODE_HOVER_DELAY);
      });
      container.on('pointerout', () => {
        this.scene.input.setDefaultCursor('default');
        this.cancelHover();
        this.tooltip.hide();
      });
      container.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev?: Phaser.Types.Input.EventData) => {
        ev?.stopPropagation?.();
        this.cancelHover();
        this.tooltip.hide();
        this.inspect(id);
        this.onGather(id);
      });
      visual = { container, rock, lastKey: '' };
      this.visuals.set(id, visual);
    } else {
      visual.container.setPosition(worldX, worldY);
      visual.container.setDepth(this.depthFor(node.y));
    }
    visual.node = node; // guardar el state actual para el mensaje de consola al click

    // Resaltado MUY sutil del área de un cardumen (una sola vez; el área no cambia).
    if (node.areaW > 0 && node.areaH > 0 && !visual.areaHighlight) {
      this.createAreaHighlight(visual, node);
    }

    if (visual.lastKey !== key) {
      this.drawNode(visual, node, app);
      visual.lastKey = key;
    }
  }

  /** Dibuja un resaltado apenas perceptible sobre los tiles del área del cardumen. */
  private createAreaHighlight(visual: NodeVisual, node: ResourceNodeStateView): void {
    const x0 = node.areaX * TILE_SIZE;
    const y0 = node.areaY * TILE_SIZE;
    const w = node.areaW * TILE_SIZE;
    const h = node.areaH * TILE_SIZE;
    const g = this.scene.add.graphics();
    g.fillStyle(0x9fd6ff, 0.05);
    g.fillRoundedRect(x0, y0, w, h, 6);
    g.lineStyle(1, 0x9fd6ff, 0.12);
    g.strokeRoundedRect(x0 + 0.5, y0 + 0.5, w - 1, h - 1, 6);
    // Bajo las entidades de esa fila (el player/cardumen pasan por encima), sobre el agua.
    g.setDepth(this.depthFor(node.areaY) - 5);
    visual.areaHighlight = g;

    // Íconos de los peces que se pescan acá, MUY tenues y chicos, al centro del área (pista de qué
    // cae). Los cardúmenes con tabla de varias especies muestran TODOS los íconos, espaciados en
    // fila, para que se vea que sueltan más de un pez (tanto en piso como en el tooltip).
    const type = getResourceNodeType(node.typeId);
    const entries = type ? getNodeProduceEntries(type) : [];
    const cx = x0 + w / 2;
    const cy = y0 + h / 2;
    const stepX = 22; // separación entre íconos cuando hay varios
    const startX = cx - ((entries.length - 1) * stepX) / 2;
    visual.areaFishIcons = [];
    entries.forEach((entry, i) => {
      const iconName = getItemTemplate(entry.itemId)?.icon;
      const iconKey = iconName ? `item_icon_${iconName}` : 'item_icon_noicon';
      const finalKey = this.scene.textures.exists(iconKey) ? iconKey : 'item_icon_noicon';
      const icon = this.scene.add
        .sprite(startX + i * stepX, cy, finalKey)
        .setDisplaySize(20, 20)
        .setAlpha(0.16)
        .setDepth(this.depthFor(node.areaY) - 4);
      visual.areaFishIcons!.push(icon);
    });
    // Shimmer lentísimo (respiración del reflejo).
    visual.areaTween = this.scene.tweens.add({
      targets: g,
      alpha: { from: 0.65, to: 1 },
      duration: 2600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Loguea en consola el nombre del nodo + cantidad restante/total, igual que los items
   * al inspeccionar un tile: `<Nombre> (actual/total)` (o "agotado" si está en respawn).
   */
  private inspect(id: string): void {
    const n = this.visuals.get(id)?.node;
    if (!n || !this.onInspect) return;
    const name = getResourceNodeType(n.typeId)?.name ?? n.typeId;
    const qty = n.depleted === 1 ? 'agotado' : `${n.remaining}/${n.maxQuantity}`;
    this.onInspect(`${name} (${qty})`);
  }

  /**
   * Textura + escala del nodo según su tipo. Ore (pico): peñasco `ore_node_<tier>_<stage>`
   * con etapa por cuánto queda. Árbol (hacha): `tree_node_<especie>[2]_<tree|stump>` (árbol
   * entero si tiene madera, tocón si agotado), mucho más grande y escalado hacia abajo.
   */
  private nodeAppearance(node: ResourceNodeStateView): { texKey: string; scale: number } {
    const type = getResourceNodeType(node.typeId);
    if (type?.toolType === 'fishing_rod') {
      // Cardumen: sprite propio `fish_node_<especie>` (arte pendiente → cae al marcador
      // procedural de cardumen en drawNode). Un solo frame (no cambia por etapa).
      return { texKey: type.id, scale: 1.0 };
    }
    if (type?.toolType === 'hatchet') {
      const species = type.id.replace('tree_node_', '');
      const suffix = node.variant === 2 ? '2' : '';
      const stage = node.depleted === 1 ? 'stump' : 'tree';
      // Escala para que el árbol (art 400×400, contenido ~245×280) se vea a TAMAÑO REAL,
      // como las decoraciones de world/trees (~205px de alto nativos): 0.8 → contenido
      // ~224px, comparable a un árbol decorativo. Ver docs/WOODCUTTING.md.
      return { texKey: `tree_node_${species}${suffix}_${stage}`, scale: 0.8 };
    }
    // El texKey sale del ID del nodo (`ore_node_<x>`), no del tier: así el carbón
    // (tier 'steel' para el gate, pero id 'ore_node_coal') usa su propio sprite.
    const baseId = type?.id ?? `ore_node_${type?.tier ?? 'iron'}`;
    const stage = nodeStage(node.remaining, node.maxQuantity, node.depleted === 1);
    // Variante visual (silueta distinta del arte fuente): variante 1 sin sufijo, 2/3 con
    // sufijo, igual que los árboles. Así dos yacimientos del mismo mineral no son iguales.
    const suffix = node.variant && node.variant > 1 ? String(node.variant) : '';
    return { texKey: `${baseId}${suffix}_${stage}`, scale: 1.0 };
  }

  /** Elige sprite real si la textura existe; si no, cae al peñasco procedural. */
  private drawNode(
    visual: NodeVisual,
    node: ResourceNodeStateView,
    app: { texKey: string; scale: number }
  ): void {
    if (this.scene.textures.exists(app.texKey)) {
      visual.rock.clear();
      if (!visual.sprite) {
        // Apoyado en el piso: origen abajo-centro, desborda el tile hacia arriba.
        visual.sprite = this.scene.add.sprite(0, TILE_SIZE / 2 - 2, app.texKey).setOrigin(0.5, 1);
        visual.container.addAt(visual.sprite, 0);
      } else {
        visual.sprite.setTexture(app.texKey);
      }
      visual.sprite.setScale(app.scale);
      this.updatePulse(visual, node.depleted === 1);
      return;
    }
    if (getResourceNodeType(node.typeId)?.toolType === 'fishing_rod') {
      this.drawCardumen(visual, node);
      return;
    }
    this.drawRock(visual, node);
  }

  /** Marcador procedural de CARDUMEN (hasta tener arte): ondas + siluetas de peces. */
  private drawCardumen(visual: NodeVisual, node: ResourceNodeStateView): void {
    const depleted = node.depleted === 1;
    const g = visual.rock;
    g.clear();
    // Ondas concéntricas suaves sobre el agua.
    g.lineStyle(1, 0x9fd6ff, depleted ? 0.15 : 0.4);
    g.strokeEllipse(0, 2, 22, 12);
    g.strokeEllipse(0, 2, 13, 7);
    if (!depleted) {
      // Tres siluetas de pez (cuerpo + cola) en tono plateado.
      g.fillStyle(0xcfe8ff, 0.85);
      const fish = [
        { x: -6, y: -1 },
        { x: 4, y: 3 },
        { x: 0, y: 6 },
      ];
      for (const f of fish) {
        g.fillEllipse(f.x, f.y, 6, 3);
        g.fillTriangle(f.x + 3, f.y, f.x + 6, f.y - 2, f.x + 6, f.y + 2);
      }
    }
    this.updatePulse(visual, depleted);
  }

  /** Dibuja el peñasco (color por tier, forma por variante, gris si agotado). */
  private drawRock(visual: NodeVisual, node: ResourceNodeStateView): void {
    const type = getResourceNodeType(node.typeId);
    const tier = type?.tier ?? 'iron';
    const depleted = node.depleted === 1;
    // El carbón (tier 'steel' para el gate) tiene su propio color oscuro en el fallback.
    const baseColor = depleted
      ? 0x6b6b6b
      : node.typeId === 'ore_node_coal'
        ? 0x26262b
        : (TIER_COLORS[tier] ?? 0x9c6b3f);

    const g = visual.rock;
    g.clear();

    // Forma base: peñasco redondeado, con leve variación por variante (seed).
    const seed = (node.variant || 1) - 1;
    const w = 20 + (seed % 3) * 2;
    const h = 14 + ((seed + 1) % 3) * 2;
    g.fillStyle(baseColor, depleted ? 0.55 : 1);
    g.fillEllipse(0, 2, w, h);
    // Sombra inferior + veta de mineral (specks) claros para dar brillo.
    g.fillStyle(0x000000, depleted ? 0.12 : 0.22);
    g.fillEllipse(0, 6, w, h * 0.5);
    const speckColor = depleted ? 0x9a9a9a : 0xffffff;
    g.fillStyle(speckColor, depleted ? 0.35 : 0.75);
    for (let i = 0; i < 3; i++) {
      const sx = -6 + ((seed + i) % 4) * 4;
      const sy = -2 + ((seed + i * 2) % 3) * 3;
      g.fillCircle(sx, sy, 1.6);
    }

    this.updatePulse(visual, depleted);
  }

  /** Pulso sutil de "respawneando" cuando el nodo está agotado. */
  private updatePulse(visual: NodeVisual, depleted: boolean): void {
    if (depleted && !visual.pulse) {
      const pulse = this.scene.add.circle(0, 0, 10, 0xffffff, 0.06);
      visual.container.add(pulse);
      visual.pulse = pulse;
      visual.pulseTween = this.scene.tweens.add({
        targets: pulse,
        alpha: { from: 0.03, to: 0.12 },
        scale: { from: 0.8, to: 1.15 },
        duration: 1400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (!depleted && visual.pulse) {
      visual.pulseTween?.remove();
      visual.pulse.destroy();
      visual.pulse = undefined;
      visual.pulseTween = undefined;
    }
  }

  public remove(id: string): void {
    const visual = this.visuals.get(id);
    if (!visual) return;
    visual.pulseTween?.remove();
    visual.areaTween?.remove();
    visual.areaHighlight?.destroy();
    visual.areaFishIcons?.forEach((s) => s.destroy());
    visual.container.destroy();
    this.visuals.delete(id);
  }

  public destroy(): void {
    this.cancelHover();
    this.tooltip.destroy();
    for (const id of Array.from(this.visuals.keys())) this.remove(id);
  }
}
